import { useCallback } from "react";

import useSWR from "swr";

import { extractAPIRequestErrorMessage } from "utils";

import { useAPI } from "./useAPI";
import { buildSwrConfig, DEFAULT_SWR_OPTIONS } from "./utils";

import type { AxiosError } from "axios";
import type { KeyedMutator, SWRConfiguration } from "swr";

interface UseSwrFetchProps {
  url: string | null;
  errorMessage: string;
  errorMessageKey?: string;
  swrOptions?: SWRConfiguration;
  onError?: (error: AxiosError) => void;
}

interface UseSwrFetchResult<T> {
  data: T | undefined;
  loading: boolean;
  validating: boolean;
  error: string | null;
  refetch: () => void;
  mutate: KeyedMutator<T>;
}

export function useSwrFetch<T>(props: UseSwrFetchProps): UseSwrFetchResult<T> {
  const { url, errorMessage, errorMessageKey, swrOptions, onError } = props;

  const { api } = useAPI();

  const {
    data,
    error: swrError,
    isLoading,
    isValidating,
    mutate,
  } = useSWR<T, AxiosError<T>>(
    url,
    async (fetchUrl: string) => {
      const response = await api<T>("get", fetchUrl);
      return response.data;
    },
    buildSwrConfig<T, AxiosError<T>>(DEFAULT_SWR_OPTIONS, swrOptions, onError),
  );

  const error = swrError
    ? extractAPIRequestErrorMessage(swrError, errorMessage, errorMessageKey)
    : null;

  const refetch = useCallback(() => {
    mutate();
  }, [mutate]);

  return {
    data,
    loading: isLoading,
    validating: isValidating,
    error,
    refetch,
    mutate,
  };
}
